import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Icon } from '@/components/ui/icon.component';
import { SheetClose, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { clearPendingSignup } from '@/store/pending-signup.store';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import LoginForm from '@/app/(main)/_components/sign-in-modal/_components/login-form.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { LoginAnalyticsEnum } from '@/types/enums/analytics/login/login-analytics.enum';
import { modalsOpeningStore, setOpenSignInModal } from '@/store/modal';

export default function LoginStep() {
  const { onClose } = useProgressiveModal();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const analytics = getAnalyticsPlugin();
  const origin = useStore(modalsOpeningStore, (state) => state.signInModalOrigin);

  useEffect(() => {
    analytics.track(LoginAnalyticsEnum.LOGIN_VIEW_MODAL, origin ? { origin } : undefined);
  }, [analytics, origin]);

  const handleCreateAccount = () => {
    analytics.track(LoginAnalyticsEnum.LOGIN_CLICK_REGISTER);
    clearPendingSignup();
    setOpenSignInModal(false);

    const query = searchParams?.toString();
    const returnUrl = query ? `${pathname}?${query}` : pathname;

    router.push(`/cadastro/?returnUrl=${encodeURIComponent(returnUrl)}`);
  };

  const handleClose = () => {
    analytics.track(LoginAnalyticsEnum.LOGIN_CLOSE_MODAL);
    onClose?.();
  };

  return (
    <div className="flex flex-col h-full md:max-h-[40rem]" data-testid={DataTestIdEnum.LOGIN_STEP}>
      <SheetHeader className="bg-white">
        <div className="flex justify-between gap-3 pb-4 items-center">
          <div className="flex gap-3">
            <SheetTitle>
              <div className="flex gap-3">
                <Typography variant={TypographyVariant.MODAL_TITLE}>Entrar</Typography>
              </div>
            </SheetTitle>
          </div>
          <SheetClose
            asChild
            data-testid={DataTestIdEnum.LOGIN_CLOSE_BUTTON}
            className="text-gray-500 cursor-pointer self-center px-2 hover:bg-gray-50"
            onClick={handleClose}
          >
            <Icon icon={faTimes} size="xl" className="hover:text-gray-900 text-[1.625rem]" />
          </SheetClose>
        </div>
      </SheetHeader>
      <div className="flex flex-col gap-6">
        <LoginForm />
        <div className="flex flex-col items-center gap-1">
          <p className="text-gray-800 font-normal leading-5">Ainda não possui conta?</p>
          <p
            data-testid={DataTestIdEnum.LOGIN_REGISTER_LINK}
            className="text-green-500 font-semibold leading-6 cursor-pointer"
            onClick={handleCreateAccount}
          >
            Cadastre-se
          </p>
        </div>
      </div>
    </div>
  );
}
